// src/people/index.js
// People module — P1: induction tracker (board + in-app CRUD).
// Bundled by esbuild → dist/people.js (globalName "People"), loaded in admin.html.
// Reads/writes /api/induction (admin Bearer) via DB.workerUrl()/DB.getToken().
// This module IS the system of record — it replaces the induction spreadsheet.
//
// Derived from the six module dates (single source of truth — see SPEC §1):
//   current module, bonus-eligible, fully-inducted, probation risk.

import { isStoreLevel } from '../shared/roles.js';   // v133.50: Area Managers and Head Office are not store staff
import { coverAwareAreaStores } from '../shared/area-cover.js';   // v136.9: own area plus any area handed over for today

let _rows = [];                    // last fetch, unfiltered
let _editId = null;                // null = adding; otherwise editing this id
const _filter = { store: '', cycle: '', q: '', am: '', country: '', leavers: '' };

// v132.142 - induction quiz EVIDENCE (the Virtual Store quiz, marked on the
// Worker against ELC Jack's questions; pass mark 80% per module). Shown
// beside M1..M6, never as them: m1_date..m6_date stay the area manager and
// Head Office sign-off, and nothing here writes them. Keyed by staff id, which
// the board learns from the directory person each row is matched to.
let _quiz = { evidence: {}, ready: false };
let _sidOf = new Map();             // board row -> staff id (rebuilt by boardRows)
async function loadQuizEvidence() {
  try {
    const res = await fetch(workerBase() + '/api/induction-quiz/evidence', { cache: 'no-store', headers: authHeaders() });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const d = await res.json();
    _quiz = { evidence: (d && d.evidence) || {}, ready: !!(d && d.ready) };
  } catch (e) { _quiz = { evidence: {}, ready: false }; }   // the board never depends on it
  drawTable();
}
// -> { attempts, passedOn, score, total } or null
function quizFor(r, i) {
  const sid = _sidOf.get(r);
  const p = sid != null ? _quiz.evidence[String(sid)] : null;
  return (p && p[i]) || null;
}

// store -> { am, country } via the live area→stores map + ALL_AREAS + the area's
// active manager. Cached per board load (reset in renderInductionBoard). v124.0.
let _amCountryMeta = null;
function metaFor(store) {
  if (!_amCountryMeta) {
    _amCountryMeta = {};
    try {
      const asm = (typeof getAreaStoreMap === 'function') ? getAreaStoreMap() : {};
      const byId = {};
      ((typeof ALL_AREAS !== 'undefined') ? ALL_AREAS : []).forEach(a => { byId[String(a.id)] = a; });
      Object.keys(asm).forEach(id => {
        const a = byId[String(id)] || {};
        const nm = (typeof getActiveAreaManagerName === 'function') ? getActiveAreaManagerName(id) : '';
        const am = (nm && nm !== 'Unassigned') ? nm : '';
        (asm[id] || []).forEach(st => { _amCountryMeta[st] = { am: am, country: a.country || '' }; });
      });
    } catch (e) {}
  }
  return _amCountryMeta[store] || { am: '', country: '' };
}

const POSITIONS = ['Sales Assistant', 'Store Manager', 'Store in-charge'];
// v132.51 - the ops manager, 25 Sep: an Area Manager had an induction record and he
// wanted it removed. There is no hard DELETE here by design (departures are
// soft), so his only option was to mark her as having LEFT the company, which
// is false and would sit in her record. not_applicable says the true thing:
// this person should never have been on the induction board.
const STATUSES  = ['active', 'not_applicable', 'resigned', 'terminated', 'left'];
const STATUS_LABELS = { not_applicable: 'not applicable (should not be on the board)' };

// ── backend ───────────────────────────────────────────────
function authHeaders() {
  const t = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const h = { 'Content-Type': 'application/json' };
  if (t) h['Authorization'] = 'Bearer ' + t;
  return h;
}
function workerBase() {
  return (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
}
function toast(msg, kind) {
  if (typeof showToast === 'function') showToast(msg, kind);
  else if (kind === 'error') console.error(msg);
}

// ── derived helpers ───────────────────────────────────────
function currentModule(r) {            // 1-6, or null when fully inducted
  for (let i = 1; i <= 6; i++) if (!r['m' + i + '_date']) return i;
  return null;
}
function allPassed(r) { return currentModule(r) === null; }
function bonusEligible(r) { return !!r.m1_date; }
// Fiscal cycle as a sortable number: "26-27" → 26, "25-26" → 25.
function cycleRank(c) { const m = String(c || '').match(/^\s*(\d+)/); return m ? Number(m[1]) : 0; }
// A "real" induction cycle is a fiscal year range like "25-26" / "26-27".
// Stray labels ("Cycle 1", "Cycle 2") must NOT appear in the cycle dropdown
// nor be the default for new records — the form was defaulting to the last
// string-sorted value, which is how "Cycle 2" was getting created. v124.1.
function isYearCycle(c) { return /^\s*\d{2,4}\s*-\s*\d{2,4}\s*$/.test(String(c || '')); }
function yearCyclesSorted() {
  return [...new Set(_rows.map(r => r.cycle).filter(isYearCycle))].sort((a, b) => cycleRank(a) - cycleRank(b));
}
function samePerson(a, b) {
  if (a.staff_ref && b.staff_ref) return String(a.staff_ref) === String(b.staff_ref);
  const n = s => String(s == null ? '' : s).trim().replace(/\s+/g, ' ').toLowerCase();
  return n(a.staff_name) === n(b.staff_name);
}
// Did this person pass module 1 in an EARLIER induction cycle? If so they are an
// existing/returning employee, not a new starter — probation does not apply.
function inductedBefore(r) {
  return _rows.some(o => o !== r && o.m1_date && cycleRank(o.cycle) < cycleRank(r.cycle) && samePerson(o, r));
}
// Current staff = active (or legacy blank) induction status. Resigned/terminated/
// left staff are hidden from the tracker by default (Matt 2026-07-13) — the
// "Include leavers" filter is the escape hatch so a mis-marked record is never lost.
function isCurrent(r) { return !r.status || r.status === 'active'; }
// v132.51 - not_applicable is not a LEAVER. Both are hidden by default, but
// lumping them together under "Include leavers" would tell somebody an Area
// Manager had left. The filter names them separately.
function isNotApplicable(r) { return r && r.status === 'not_applicable'; }
// Probation end: the explicit date when set, else joining date + 6 months (the
// standard probation term — new joiners flag automatically once their joining
// date is on the record, no separate data entry).
// v132.51 - the staff record is the fallback source of a joining date.
// The ops manager: probation badges were inconsistent because induction.joining_date
// is typed by hand per record and mostly was not. staff.joining_date (v219-0)
// is a fact about the PERSON, so filling it once fixes every cycle they
// appear in. Matched on emp_code first, then name, the same way the board
// already matches a record to a directory person.
function staffJoiningDate(r) {
  try {
    if (typeof staffData === 'undefined' || !Array.isArray(staffData)) return null;
    const hit = staffData.find(s => s && recMatchesDir(r, s));
    return (hit && hit.joining_date) ? String(hit.joining_date).slice(0, 10) : null;
  } catch (e) { return null; }
}
function probationEndOf(r) {
  if (r.probation_end) return String(r.probation_end).slice(0, 10);
  // induction.joining_date WINS where set: somebody typed it, possibly to
  // correct a bad HR date, and an import must not silently overrule that.
  const joined = r.joining_date || staffJoiningDate(r);
  if (joined) {
    const d = new Date(String(joined).slice(0, 10) + 'T12:00:00Z');
    if (!Number.isNaN(d.getTime())) { d.setUTCMonth(d.getUTCMonth() + 6); return d.toISOString().slice(0, 10); }
  }
  return null;
}
// On probation = within the 6-month window (any new joiner), unless they were
// inducted in a prior cycle (returning/existing employee).
function onProbation(r) {
  if (inductedBefore(r)) return false;
  const end = probationEndOf(r);
  if (!end) return false;
  const t = new Date(end + 'T12:00:00Z').getTime();
  return !Number.isNaN(t) && t >= Date.now();
}
function probationRisk(r) {
  if (allPassed(r)) return false;
  if (inductedBefore(r)) return false;           // did module 1 in a prior cycle — not on probation
  const endStr = probationEndOf(r);
  if (!endStr) return false;
  const end = new Date(endStr + 'T12:00:00Z').getTime();
  if (Number.isNaN(end)) return false;
  const days = (end - Date.now()) / 86400000;
  return days >= 0 && days <= 30;   // ends within 30 days (not already over), not inducted
}

// ── scope + filter ────────────────────────────────────────
// Returns null = no restriction (Head Office), or an ARRAY of store names the
// viewer may see. An array is a restriction even when EMPTY (fail closed).
//
// Overnight fix (sensitive): this bundle only loads in admin.html, and
// getMyStores() exists only in index.html, so this always returned null and
// an area manager's induction board listed every staff member in the company.
// admin.html's own rule (getVisibleStaff / canEditStaff) is the fallback:
// Head Office everything, an area manager their area's stores
// (AREA_STORE_MAP), a store manager their store, anyone else nothing.
// The Worker now enforces the same scope on every induction write.
function adminScopeStores() {
  if (typeof currentAdmin === 'undefined' || !currentAdmin) return null;
  const role = String(currentAdmin.role || '').toLowerCase();
  if (role === 'admin') return null;
  if (role === 'area_manager') {
    // v136.9: own area plus any area handed over for today (src/shared/area-cover.js)
    return (typeof AREA_STORE_MAP !== 'undefined' && AREA_STORE_MAP) ? coverAwareAreaStores(currentAdmin, AREA_STORE_MAP) : [];
  }
  if (role === 'store_manager') return currentAdmin.store ? [currentAdmin.store] : [];
  return [];
}
function myStores() {
  try { return (typeof getMyStores === 'function') ? getMyStores() : adminScopeStores(); }
  catch (e) { return []; }   // fail closed: an error shows nothing extra
}
function storeOptionList(includeStore) {
  const scope = myStores();
  let list = Array.isArray(scope) ? scope.slice()
           : [...new Set(_rows.map(r => r.store).filter(Boolean))];
  if (includeStore && !list.includes(includeStore)) list = list.concat(includeStore);
  return list.sort();
}
// Active staff from the directory (scoped), for the "Add Person" picker — every
// induction record is created FROM a directory person so staff_ref stays linked.
function directoryStaff() {
  try {
    if (typeof staffData === 'undefined' || !Array.isArray(staffData)) return [];
    const scope = myStores();
    return staffData
      .filter(s => s && s.active !== false && s.name && isStoreLevel(s.role))   // v133.50: area managers and Head Office are not on a store induction board
      .filter(s => !Array.isArray(scope) || scope.includes(s.store))   // #1758: an empty scope is nobody, never everybody
      .slice()
      .sort((a, b) => String(a.store).localeCompare(String(b.store)) || String(a.name).localeCompare(String(b.name)));
  } catch (e) { return []; }
}
function roleToPosition(role) {
  const r = String(role || '').toLowerCase();
  if (r.indexOf('manager') !== -1 || r.indexOf('charge') !== -1) return 'Store Manager';
  return 'Sales Assistant';
}
// Latest fiscal cycle present in the data (highest "26-27" → 26).
function latestCycle() {
  const cs = _rows.map(r => r.cycle).filter(Boolean);
  if (!cs.length) return _filter.cycle || '';
  return cs.slice().sort((a, b) => cycleRank(a) - cycleRank(b)).pop();
}
// Match an induction record to a directory person (emp_code first, name fallback).
function recMatchesDir(r, s) {
  if (r.staff_ref && s.empCode) return String(r.staff_ref) === String(s.empCode);
  const n = x => String(x == null ? '' : x).trim().replace(/\s+/g, ' ').toLowerCase();
  return n(r.staff_name) === n(s.name);
}
// A 0/6 placeholder for a directory person who has no induction record yet.
// id=null marks it as a shell — clicking it opens the modal in create mode.
function shellFor(s, cycle) {
  return {
    id: null, _shell: true,
    staff_name: s.name, staff_ref: s.empCode || null,
    store: s.store || '', position: roleToPosition(s.role),
    cycle: cycle, status: 'active',
    // m1_date..m6_date intentionally absent → currentModule() returns 1 (0 passed)
  };
}
function passFilter(r) {
  const scope = myStores();
  // v132.51 - three states: current (default), not-applicable only, everything.
  if (_filter.leavers === 'na') { if (!isNotApplicable(r)) return false; }
  else if (_filter.leavers !== 'all' && !isCurrent(r)) return false;   // current staff only (default)
  if (Array.isArray(scope) && !scope.includes(r.store)) return false;
  if (_filter.store && r.store !== _filter.store) return false;
  if (_filter.cycle && r.cycle !== _filter.cycle) return false;
  if (_filter.q && !(r.staff_name || '').toLowerCase().includes(_filter.q.toLowerCase())) return false;
  if (_filter.am && metaFor(r.store).am !== _filter.am) return false;
  if (_filter.country && metaFor(r.store).country !== _filter.country) return false;
  return true;
}
// Directory-driven board: ONE row per active directory person — their induction
// record overlaid, or a 0/6 shell if they have none yet. People are added in the
// Directory; they appear here automatically. Records whose person isn't in the
// (scoped) directory (left/transferred) still show so nothing is lost. If the
// directory hasn't loaded yet, fall back to records-only so the board is never blank.
function boardRows() {
  const dir = directoryStaff();
  if (!dir.length) return _rows.filter(passFilter);
  const latest = latestCycle();
  const used = new Set();
  _sidOf = new Map();
  let merged = dir.map(s => {
    const cands = _rows.filter(r => recMatchesDir(r, s));
    cands.forEach(r => used.add(r));
    let rec = _filter.cycle
      ? cands.find(r => r.cycle === _filter.cycle)
      : cands.slice().sort((a, b) => cycleRank(a.cycle) - cycleRank(b.cycle)).pop();
    if (rec) { _sidOf.set(rec, s.id); return rec; }
    // v133.50: marked "not applicable" in ANY season keeps someone off the board (it used to come back each new season as 0/6)
    if (cands.some(r => r && r.status === 'not_applicable')) return null;
    // No record: show a 0/6 shell, but only in the current-cycle / all-cycles view
    // (don't fabricate shells into a past cycle the person wasn't part of).
    if (!_filter.cycle || _filter.cycle === latest) { const sh = shellFor(s, _filter.cycle || latest); _sidOf.set(sh, s.id); return sh; }
    return null;
  }).filter(Boolean);
  _rows.forEach(r => { if (!used.has(r)) merged.push(r); });   // orphan records
  return merged
    .filter(passFilter)
    .sort((a, b) => String(a.store || '').localeCompare(String(b.store || '')) ||
                    String(a.staff_name || '').localeCompare(String(b.staff_name || '')));
}

// ── board ─────────────────────────────────────────────────
async function renderInductionBoard() {
  const host = document.getElementById('page-induction');
  if (!host) return;
  host.innerHTML = '<div class="card"><div style="color:var(--sub);font-size:13px;padding:10px 0">Loading induction…</div></div>';
  try {
    const res = await fetch(workerBase() + '/api/induction', { cache: 'no-store', headers: authHeaders() });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    _rows = await res.json();
    if (!Array.isArray(_rows)) _rows = [];
  } catch (e) {
    host.innerHTML = '<div class="card"><div style="color:var(--arota-red-ink);font-size:13px;padding:10px 0">Couldn\u2019t load induction data: ' + (e.message || e) + '</div></div>';
    return;
  }

  const cycles = yearCyclesSorted();
  if (!_filter.cycle && cycles.length) _filter.cycle = cycles[cycles.length - 1];

  const esc = window.escapeHtml || (s => s);
  // v135.2: names (stores, area managers) stay as written in Arabic: translate="no"
  const opt = (v, label, sel, asWritten) => '<option value="' + esc(v) + '"' + (sel ? ' selected' : '') + (asWritten ? ' translate="no"' : '') + '>' + esc(label) + '</option>';
  const storeOpts = opt('', 'All stores', !_filter.store) + storeOptionList().map(s => opt(s, s, _filter.store === s, true)).join('');
  const cycleOpts = opt('', 'All cycles', !_filter.cycle) + cycles.map(c => opt(c, c, _filter.cycle === c)).join('');
  // Area-manager + country filter options, derived from the staff in scope. v124.0.
  _amCountryMeta = null;
  const _amSet = {}, _ctrySet = {}, _seen = new Set();
  directoryStaff().forEach(s => { if (s.store) _seen.add(s.store); });
  _rows.forEach(r => { if (r.store) _seen.add(r.store); });
  _seen.forEach(st => { const m = metaFor(st); if (m.am) _amSet[m.am] = true; if (m.country) _ctrySet[m.country] = true; });
  const amOpts = opt('', 'All area managers', !_filter.am) + Object.keys(_amSet).sort().map(a => opt(a, a, _filter.am === a, true)).join('');
  const ctryOpts = opt('', 'All countries', !_filter.country) + Object.keys(_ctrySet).sort().map(c => opt(c, c, _filter.country === c)).join('');

  host.innerHTML =
    '<div class="stats-grid" id="indSummary" style="margin-bottom:16px"></div>' +
    '<div class="card">' +
      '<div class="section-header">' +
        '<div class="section-title">Induction tracker</div>' +
      '</div>' +
      '<div class="filter-row">' +
        '<input type="text" class="filter-input" placeholder="Search name\u2026" value="' + (_filter.q || '') + '" oninput="People.induction.setFilter(\'q\', this.value)" style="min-width:200px">' +
        '<select class="filter-input" onchange="People.induction.setFilter(\'store\', this.value)">' + storeOpts + '</select>' +
        '<select class="filter-input" onchange="People.induction.setFilter(\'cycle\', this.value)">' + cycleOpts + '</select>' +
        '<select class="filter-input" onchange="People.induction.setFilter(\'am\', this.value)">' + amOpts + '</select>' +
        '<select class="filter-input" onchange="People.induction.setFilter(\'country\', this.value)">' + ctryOpts + '</select>' +
        '<select class="filter-input" onchange="People.induction.setFilter(\'leavers\', this.value)">' +
          opt('', 'Current staff', !_filter.leavers) +
          // v132.51 - a separate option, because an Area Manager who should
          // never have been on the board has not "left".
          opt('na', 'Not applicable only', _filter.leavers === 'na') +
          opt('all', 'Include leavers', _filter.leavers === 'all') +
        '</select>' +
      '</div>' +
      '<div class="table-wrap"><table class="data-table">' +
        '<thead><tr><th>Name</th><th>Store</th><th>Role</th><th>Modules</th><th>Status</th><th>Flags</th></tr></thead>' +
        '<tbody id="indTableBody"></tbody>' +
      '</table></div>' +
      '<div id="indQuizNote" style="font-size:12px;color:var(--sub);margin-top:10px" hidden></div>' +
    '</div>';

  drawTable();
  loadQuizEvidence();   // v132.142: the quiz evidence follows; the board never waits for it
}

function drawTable() {
  const sumEl = document.getElementById('indSummary');
  const bodyEl = document.getElementById('indTableBody');
  if (!sumEl || !bodyEl) return;
  const esc = window.escapeHtml || (s => s);
  const fmt = window.formatDDMMYYYY || (s => s);
  const rows = boardRows();
  const qn = document.getElementById('indQuizNote');
  if (qn) {
    const any = _quiz.ready && rows.some(r => { for (let i = 1; i <= 6; i++) { const q = quizFor(r, i); if (q && q.passedOn) return true; } return false; });
    qn.hidden = !any;
    qn.textContent = 'A dashed green ring means the person passed the induction quiz for that module in the Virtual Store. It is evidence for the area manager, not a sign-off: only a date entered here signs a module off. Hover a module for the details.';
  }

  const total = rows.length;
  const inducted = rows.filter(allPassed).length;
  const risk = rows.filter(probationRisk).length;
  const probation = rows.filter(r => probationRisk(r) || onProbation(r)).length;
  const pct = total ? Math.round((inducted / total) * 100) : 0;

  sumEl.innerHTML =
    '<div class="stat-card"><div class="stat-num">' + total + '</div><div class="stat-label">People</div></div>' +
    '<div class="stat-card"><div class="stat-num">' + pct + '%</div><div class="stat-label">Fully inducted</div></div>' +
    '<div class="stat-card"><div class="stat-num">' + (total - inducted) + '</div><div class="stat-label">In progress</div></div>' +
    '<div class="stat-card"><div class="stat-num">' + probation + '</div><div class="stat-label">On probation</div></div>' +
    '<div class="stat-card"><div class="stat-num' + (risk ? ' amber' : '') + '">' + risk + '</div><div class="stat-label">Probation risk</div></div>';

  if (!total) {
    bodyEl.innerHTML = '<tr><td colspan="6" style="text-align:center;color:var(--sub);padding:24px 0">No staff found. People appear here automatically once added in the Directory.</td></tr>';
    return;
  }

  bodyEl.innerHTML = rows.map(r => {
    const cur = currentModule(r);
    let dots = '';
    for (let i = 1; i <= 6; i++) {
      const d = r['m' + i + '_date'];
      const passed = !!d, isCur = (cur === i);
      // v132.158: a module FAILED (recorded by Head Office after the area manager's
      // assessment) is red, not the blue "not done yet" ring. A sign-off always wins.
      const failedOn = !passed && r['m' + i + '_failed_date'] ? String(r['m' + i + '_failed_date']).slice(0, 10) : null;
      const bg = passed ? 'var(--green)' : (failedOn ? 'var(--red)' : 'var(--ap-chip-off)');
      const fg = passed || failedOn ? '#fff' : 'var(--sub)';
      const ring = isCur ? 'box-shadow:0 0 0 2px ' + (failedOn ? 'var(--red)' : 'var(--blue)') + ';' : '';
      const qz = quizFor(r, i);
      let title = passed ? ('Module ' + i + ' passed ' + fmt(String(d).slice(0, 10)))
        : failedOn ? ('Module ' + i + ' FAILED ' + fmt(failedOn) + (r.next_attempt_date ? ' · next attempt ' + fmt(String(r.next_attempt_date).slice(0, 10)) : ''))
        : ('Module ' + i + ' not passed');
      if (qz && qz.passedOn) title += ' \u00b7 induction quiz passed ' + fmt(String(qz.passedOn).slice(0, 10)) + ' (' + qz.score + ' of ' + qz.total + ')' + (passed ? '' : ': evidence only, the area manager signs the module off');
      else if (qz && qz.attempts) title += ' \u00b7 induction quiz tried ' + qz.attempts + (qz.attempts === 1 ? ' time' : ' times') + ', not passed yet';
      // a quiz pass on a module not yet signed off: a dashed green ring, never the filled green of a sign-off
      const evid = !passed && qz && qz.passedOn ? 'outline:2px dashed var(--green);outline-offset:-2px;color:var(--arota-green-ink);' : '';
      dots += '<span title="' + esc(title) + '" style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;font-size:10px;font-weight:700;margin-inline-end:3px;background:' + bg + ';color:' + fg + ';' + ring + evid + '">' + i + '</span>';
    }
    const statusTxt = cur === null
      ? '<span style="color:var(--arota-green-ink);font-weight:600">\u2713 Inducted</span>'
      : r['m' + cur + '_failed_date']   // v132.158
        ? '<span style="color:var(--arota-red-ink);font-weight:700">Module ' + cur + ' failed</span>'
        : '<span style="color:var(--sub)">Module ' + cur + ' of 6</span>';
    let flags = '';
    if (bonusEligible(r)) flags += '<span style="background:var(--gp);color:var(--arota-green-ink);font-size:10px;font-weight:700;padding:2px 7px;border-radius:6px;margin-inline-end:5px">BONUS</span>';
    if (probationRisk(r)) flags += '<span title="Probation ends ' + esc(fmt(String(probationEndOf(r) || '').slice(0, 10))) + ': induction not complete" style="background:var(--warn-bg);color:var(--warn-deep);font-size:10px;font-weight:700;padding:2px 7px;border-radius:6px;margin-inline-end:5px">PROBATION \u26a0</span>';
    else if (onProbation(r)) flags += '<span title="On probation until ' + esc(fmt(String(probationEndOf(r) || '').slice(0, 10))) + ' (6 months from joining)" style="background:var(--info-bg);color:var(--info-deep);font-size:10px;font-weight:700;padding:2px 7px;border-radius:6px;margin-inline-end:5px">PROBATION</span>';
    if (r.status && r.status !== 'active') flags += '<span style="background:var(--danger-bg);color:var(--danger-deep);font-size:10px;font-weight:700;padding:2px 7px;border-radius:6px">' + esc(String(r.status).toUpperCase()) + '</span>';
    // v132.50 - a row carrying AM feedback says so, otherwise the note is
    // invisible until somebody happens to open that person. The next attempt
    // date rides the tooltip: it is the thing being chased.
    if (r.am_feedback) {
      const nx = r.next_attempt_date ? (' — next attempt ' + fmt(String(r.next_attempt_date).slice(0, 10))) : '';
      flags += '<span title="' + esc(String(r.am_feedback).slice(0, 300) + nx) + '" style="background:var(--ap-indigo-bg);color:var(--ap-indigo-deep);font-size:10px;font-weight:700;padding:2px 7px;border-radius:6px;margin-inline-start:5px">FEEDBACK</span>';
    }

    const onClick = r.id
      ? "People.induction.openInductionModal('" + esc(String(r.id)) + "')"
      : "People.induction.openInductionModal('', '" + esc(String(r.staff_ref || r.staff_name || '')) + "')";
    return '<tr style="cursor:pointer" onclick="' + onClick + '">' +
      '<td><span translate="no">' + esc(r.staff_name || '') + '</span>' + (r.staff_ref ? '' : ' <span title="not yet matched to a staff record" style="color:var(--adm-amber-text)">\u25cf</span>') + '</td>' +
      '<td translate="no" style="color:var(--sub)">' + esc(r.store || '') + '</td>' +
      '<td style="color:var(--sub)">' + esc(r.position || '') + '</td>' +
      '<td style="white-space:nowrap">' + dots + '</td>' +
      '<td>' + statusTxt + '</td>' +
      '<td>' + (flags || '<span style="color:var(--sub)">\u2014</span>') + '</td>' +
    '</tr>';
  }).join('');
}

function setFilter(key, val) { _filter[key] = val; drawTable(); }

// ── add / edit / stamp modal ──────────────────────────────
function ensureModal() {
  let ov = document.getElementById('inductionModal');
  if (ov) return ov;
  ov = document.createElement('div');
  ov.className = 'modal-overlay';
  ov.id = 'inductionModal';
  const fld = (label, inner, full) =>
    '<div class="form-group' + (full ? ' full' : '') + '"><div class="form-label">' + label + '</div>' + inner + '</div>';
  const modDates = [1, 2, 3, 4, 5, 6].map(i =>
    fld('Module ' + i, '<input type="date" class="form-input" id="indmM' + i + '">')).join('');
  ov.innerHTML =
    '<div class="modal">' +
      '<div class="modal-header"><h3 id="indmTitle">Add Person</h3><p>Induction record — stamp module pass dates here</p></div>' +
      '<div class="modal-body"><div class="form-grid">' +
        '<div class="form-group full" id="indmDirGroup"><div class="form-label">Staff member <span style="color:var(--arota-red-ink)">*</span></div>' +
          '<select class="form-select" id="indmDir" onchange="People.induction.pickStaff(this.value)"></select>' +
          '<div style="font-size:11px;color:var(--sub);margin-top:4px">Pulled from the staff directory \u2014 keeps the induction record linked to a real staff record.</div></div>' +
        '<div class="form-group full" id="indmNameGroup"><div class="form-label">Full Name <span style="color:var(--arota-red-ink)">*</span></div>' +
          '<input type="text" class="form-input" id="indmName" placeholder="Select a staff member above" readonly></div>' +
        fld('Store <span style="color:var(--arota-red-ink)">*</span>', '<select class="form-select" id="indmStore"></select>') +
        fld('Role', '<select class="form-select" id="indmPosition">' + POSITIONS.map(p => '<option>' + p + '</option>').join('') + '</select>') +
        fld('Cycle <span style="color:var(--arota-red-ink)">*</span>', '<input type="text" class="form-input" id="indmCycle" placeholder="e.g. 26-27">') +
        fld('Emp Code (linked)', '<input type="text" class="form-input" id="indmRef" placeholder="auto-filled from directory" readonly>') +
        fld('Joining date', '<input type="date" class="form-input" id="indmJoining">') +
        fld('Probation end', '<input type="date" class="form-input" id="indmProbation">') +
        modDates +
        fld('Language', '<select class="form-select" id="indmLang"><option value="">\u2014</option><option value="EN">EN</option><option value="AR">AR</option></select>') +
        fld('Status', '<select class="form-select" id="indmStatus">' + STATUSES.map(s => '<option value="' + s + '">' + (STATUS_LABELS[s] || s) + '</option>').join('') + '</select>' +
          '<div style="font-size:11px;color:var(--sub);margin-top:4px">Resigned / terminated / left staff are hidden from the tracker (use the "Include leavers" filter to see them).</div>') +
        fld('Comment', '<input type="text" class="form-input" id="indmComment">', true) +
        // v132.50 - the ops manager: "no option to feed feedback info on reasons,
        // next given dates" for a failed induction. Per person rather than a
        // shared header box (Matt's call): why THIS person failed belongs on
        // THIS person's record.
        '<div class="form-group full" id="indmFeedbackGroup" style="border-top:1px solid var(--border);margin-top:6px;padding-top:12px">' +
          '<div class="form-label">Area manager feedback <span style="font-weight:400;color:var(--sub)">(for a failed induction)</span></div>' +
          '<textarea class="form-input" id="indmFeedback" rows="3" maxlength="4000" placeholder="Why it was not passed, and what needs to happen before the next attempt"></textarea>' +
          '<div id="indmFeedbackWho" style="font-size:11px;color:var(--sub);margin-top:4px"></div>' +
        '</div>' +
        fld('Next attempt date', '<input type="date" class="form-input" id="indmNextAttempt">') +
        // v132.158 - a FAILED module, recorded by Head Office after the area manager's
        // assessment in person. Shown red on the board. Head Office only, an edit only,
        // and only once migration v227-0 has added the columns (openInductionModal).
        '<div class="form-group full" id="indmFailedGroup" hidden>' +
          '<div class="form-label">Failed on <span style="font-weight:400;color:var(--sub)">(the date a module was failed in the assessment by the area manager; leave blank if not failed)</span></div>' +
          '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:8px">' +
            [1, 2, 3, 4, 5, 6].map(i => '<label style="font-size:12px;color:var(--sub)">Module ' + i + '<input type="date" class="form-input" id="indmF' + i + '"></label>').join('') +
          '</div></div>' +
      '</div></div>' +
      '<div class="modal-footer">' +
        '<button class="btn btn-outline" onclick="People.induction.closeInductionModal()">Cancel</button>' +
        '<button class="btn btn-green" id="indmSave" onclick="People.induction.saveInduction()">Save</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(ov);
  return ov;
}

function openInductionModal(id, prefillKey) {
  const ov = ensureModal();
  _editId = id || null;
  const row = id ? _rows.find(r => String(r.id) === String(id)) : null;
  // Shell click: a directory person with no record yet — create mode, person fixed.
  const pre = (!row && prefillKey)
    ? directoryStaff().find(x => String(x.empCode || x.name) === String(prefillKey))
    : null;
  const cycles = yearCyclesSorted();
  const defaultCycle = (isYearCycle(_filter.cycle) ? _filter.cycle : '') || (cycles.length ? cycles[cycles.length - 1] : '');
  const esc = window.escapeHtml || (s => s);

  // store dropdown (scoped, plus the row's / prefilled person's own store)
  const ownStore = (row && row.store) || (pre && pre.store);
  const stores = storeOptionList(ownStore);
  document.getElementById('indmStore').innerHTML =
    stores.map(s => '<option value="' + esc(s) + '"' + (ownStore === s ? ' selected' : '') + ' translate="no">' + esc(s) + '</option>').join('');

  // Directory picker — only the legacy free-add path shows it. Editing a record OR
  // starting induction for a specific directory person both fix the person, so hide it.
  const dirGroup = document.getElementById('indmDirGroup');
  const dirSel = document.getElementById('indmDir');
  if (row || pre) {
    if (dirGroup) dirGroup.style.display = 'none';
  } else {
    if (dirGroup) dirGroup.style.display = '';
    if (dirSel) {
      const list = directoryStaff();
      dirSel.innerHTML = '<option value="">\u2014 Select a staff member \u2014</option>' +
        list.map(s => '<option value="' + esc(String(s.empCode || s.name)) + '" translate="no">' + esc(s.name) + ' \u2014 ' + esc(s.store || '') + '</option>').join('');
      dirSel.value = '';
    }
  }

  const v = (suffix, val) => { const el = document.getElementById('indm' + suffix); if (el) el.value = val || ''; };
  document.getElementById('indmTitle').textContent =
    row ? 'Edit Person' : (pre ? 'Start induction \u2014 ' + (pre.name || '') : 'Add Person');
  v('Name', row ? row.staff_name : (pre ? pre.name : ''));
  v('Cycle', row ? row.cycle : defaultCycle);
  v('Ref', row ? row.staff_ref : (pre ? (pre.empCode || '') : ''));
  v('Joining', row && row.joining_date ? String(row.joining_date).slice(0, 10) : '');
  v('Probation', row && row.probation_end ? String(row.probation_end).slice(0, 10) : '');
  for (let i = 1; i <= 6; i++) v('M' + i, row && row['m' + i + '_date'] ? String(row['m' + i + '_date']).slice(0, 10) : '');
  v('Comment', row ? row.comment : '');
  document.getElementById('indmPosition').value = (row && row.position) || (pre ? roleToPosition(pre.role) : 'Sales Assistant');
  document.getElementById('indmLang').value = (row && row.language) || '';
  document.getElementById('indmStatus').value = (row && row.status) || 'active';
  // v132.50 - the feedback pair, plus its attribution line.
  v('Feedback', row ? row.am_feedback : '');
  v('NextAttempt', row && row.next_attempt_date ? String(row.next_attempt_date).slice(0, 10) : '');
  // v132.158 - the failed dates: Head Office (the feedback was not withheld), an
  // existing record, and the columns exist (v227-0 run: the key comes back, even null).
  const failedGroup = document.getElementById('indmFailedGroup');
  if (failedGroup) {
    const showFailed = !!(row && Object.prototype.hasOwnProperty.call(row, 'm1_failed_date') && !row.am_feedback_hidden);
    failedGroup.hidden = !showFailed;
    for (let i = 1; i <= 6; i++) v('F' + i, showFailed && row['m' + i + '_failed_date'] ? String(row['m' + i + '_failed_date']).slice(0, 10) : '');
  }
  const whoEl = document.getElementById('indmFeedbackWho');
  const fbEl = document.getElementById('indmFeedback');
  if (whoEl) {
    if (row && row.am_feedback_hidden) {
      // The Worker withheld it: this reader is not Head Office and not this
      // store's area manager. Say so, rather than showing an empty box that
      // reads as 'nobody has written anything'.
      whoEl.textContent = 'Head Office only. Feedback is not visible to area managers or stores.';
      if (fbEl) { fbEl.value = ''; fbEl.placeholder = 'Hidden'; fbEl.disabled = true; }
    } else {
      if (fbEl) { fbEl.disabled = false; fbEl.placeholder = 'Why it was not passed, and what needs to happen before the next attempt'; }
      whoEl.textContent = (row && row.am_feedback_by)
        // v133.83 (sweep H): fmt lives in the board's render, not here; a record with feedback could not reopen
        ? ('Last written by ' + row.am_feedback_by + (row.am_feedback_at ? ' on ' + (window.formatDDMMYYYY || ((s) => s))(String(row.am_feedback_at).slice(0, 10)) : ''))
        : '';
    }
  }

  ov.classList.add('open');
}

// Fill the form from the chosen directory person — keeps staff_ref linked.
function pickStaff(val) {
  const s = directoryStaff().find(x => String(x.empCode || x.name) === String(val));
  const set = (suffix, v) => { const el = document.getElementById('indm' + suffix); if (el) el.value = v || ''; };
  if (!s) { set('Name', ''); set('Ref', ''); return; }
  set('Name', s.name);
  set('Ref', s.empCode || '');
  const storeSel = document.getElementById('indmStore');
  if (storeSel && s.store) {
    let found = false;
    for (let i = 0; i < storeSel.options.length; i++) { if (storeSel.options[i].value === s.store) { storeSel.selectedIndex = i; found = true; break; } }
    if (!found) { const o = document.createElement('option'); o.value = s.store; o.textContent = s.store; storeSel.appendChild(o); storeSel.value = s.store; }
  }
  const posSel = document.getElementById('indmPosition');
  if (posSel) posSel.value = roleToPosition(s.role);
}

function closeInductionModal() {
  const ov = document.getElementById('inductionModal');
  if (ov) ov.classList.remove('open');
  _editId = null;
}

async function saveInduction() {
  const get = suffix => { const el = document.getElementById('indm' + suffix); return el ? el.value.trim() : ''; };
  const dateOrNull = suffix => { const el = document.getElementById('indm' + suffix); return el && el.value ? el.value : null; };

  const payload = {
    staff_name: get('Name'),
    store: get('Store'),
    cycle: get('Cycle'),
    position: get('Position'),
    staff_ref: get('Ref') || null,
    joining_date: dateOrNull('Joining'),
    probation_end: dateOrNull('Probation'),
    m1_date: dateOrNull('M1'), m2_date: dateOrNull('M2'), m3_date: dateOrNull('M3'),
    m4_date: dateOrNull('M4'), m5_date: dateOrNull('M5'), m6_date: dateOrNull('M6'),
    language: get('Lang') || null,
    status: get('Status') || 'active',
    comment: get('Comment') || null,
  };
  // v132.50 - feedback rides the same save, but only on an EDIT. Creating a
  // person and immediately recording why they failed makes no sense, and
  // sending the fields on a POST would stamp an author on an empty note.
  // Omitted entirely when untouched, so an ordinary edit never rewrites the
  // attribution of somebody else's feedback.
  if (_editId) {
    const fbEl2 = document.getElementById('indmFeedback');
    if (fbEl2 && !fbEl2.disabled) {
      payload.am_feedback = fbEl2.value.trim() || null;
      payload.next_attempt_date = dateOrNull('NextAttempt');
    }
    // v132.158 - only when the failed dates were shown (Head Office, columns exist), so an
    // ordinary edit never sends them and never trips the Head Office check or a missing column.
    const fg = document.getElementById('indmFailedGroup');
    if (fg && !fg.hidden) {
      for (let i = 1; i <= 6; i++) payload['m' + i + '_failed_date'] = dateOrNull('F' + i);
    }
  }
  if (!payload.staff_name || !payload.store || !payload.cycle) {
    toast(_editId ? 'Name, store and cycle are required' : 'Select a staff member from the directory (and set the cycle)', 'error');
    return;
  }

  const saveBtn = document.getElementById('indmSave');
  if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }
  try {
    const editing = !!_editId;
    const url = workerBase() + '/api/induction' + (editing ? '?id=' + encodeURIComponent(_editId) : '');
    const res = await fetch(url, {
      method: editing ? 'PATCH' : 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) { const t = await res.text(); throw new Error('HTTP ' + res.status + ' ' + t.slice(0, 160)); }
    toast(editing ? 'Induction updated' : 'Person added', 'success');
    closeInductionModal();
    await renderInductionBoard();
  } catch (e) {
    toast('Save failed: ' + (e.message || e), 'error');
  } finally {
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
  }
}

export const induction = {
  renderInductionBoard, setFilter,
  openInductionModal, closeInductionModal, saveInduction, pickStaff,
};
